<template>
  <MainLayout>
    <ContentPageHeader functionName="棚卸管理" pageName="棚卸一覧">
    </ContentPageHeader>
    
    <div class="search-section">
      <div class="search-form">
        <div class="store-info">
          <span class="store-name">{{ userStoreName }}</span>
          <span class="inventory-date">{{ latestInventoryDate }}</span>
        </div>
        
        <FormTextField 
          label="商品コード" 
          id="productCode" 
          v-model="searchParams.productCode"
          size="medium"
          :disabled="isInventoryComplete"
        />
        
        <FormRadioField 
          label="対象選択" 
          id="targetSelection" 
          v-model="searchParams.showScannedOnly"
          :options="targetOptions"
        />
      </div>
    </div>
    
    <div class="search-result">
      <FormListTable
        :items="inventoryList"
        :columns="columns"
        :total-items="totalItems"
        :page="currentPage"
        :per-page="50"
        :isToggleDisplay="false"
        @page-change="onPageChange"
        empty-message="棚卸データがありません"
      />
    </div>

    <template #footer-buttons>
      <button class="btn btn-secondary" @click="downloadInventory">ダウンロード</button>
      <button class="btn btn-primary" @click="bulkUpdateInventory" :disabled="isInventoryComplete">在庫一括更新</button>
    </template>
  </MainLayout>
</template>

<script>
import MainLayout from '@/components/layout/MainLayout.vue'
import ContentPageHeader from '@/components/layout/ContentPageHeader.vue'
import FormTextField from '@/components/form/FormTextField.vue'
import FormRadioField from '@/components/form/FormRadioField.vue'
import FormListTable from '@/components/form/FormListTable.vue'
import service from '../../services/service';
import { showLoading, hideLoading } from '@/utils/loadingEventBus'
import constants from '@/constants/constants.json';

export default {
  name: 'InventoryListView',
  components: {
    MainLayout,
    ContentPageHeader,
    FormTextField,
    FormRadioField,
    FormListTable
  },
  data() {
    return {
      searchParams: {
        productCode: '',
        showScannedOnly: 0
      },
      inventoryList: [],
      isInventoryComplete: true,
      totalItems: 0,
      currentPage: 0,
      perPage: 50,
      totalCount: 0,
      userStoreName: '',
      latestInventoryDate: '',
      categoryOptions: [],
      manufacturerOptions: [],
      currentInventoryId: null,
      targetOptions: [
        { value: 0, label: '未スキャンのみ表示' },
        { value: 1, label: 'スキャン済含む表示' }
      ],
      columns: [
        { key: 'no', label: 'No', minWidth: 80 },
        { key: 'categoryName', label: 'カテゴリ', minWidth: 120 },
        { key: 'makerName', label: 'メーカー', minWidth: 120 },
        { key: 'productCode', label: '商品コード', minWidth: 120 },
        { key: 'productName', label: '商品名', minWidth: 200 },
        { key: 'currentStockNumber', label: '現在個数', minWidth: 100 },
        { key: 'actualInventoryNumber', label: '実在庫数', minWidth: 100 }
      ]
    }
  },
  watch: {
    // 対象選択（未スキャン/スキャン済）が変更された時の処理
    'searchParams.showScannedOnly'() {
      this.currentPage = 0;
      this.loadInventoryList();
    },
    // 商品コードが10桁入力された時の処理
    'searchParams.productCode'(newVal) {
      if (newVal && newVal.length === constants.PRODUCT_CODE_LENGTH) {
        this.processProductCode();
      }
    }
  },
  async mounted() {
    this.userStoreName = this.$store.getters['auth/currentUser']?.storeName || '';
    // 初期データを取得し、成功した場合のみ棚卸一覧を読み込む
    const isInventoryFind = await this.getInitData();
    if (isInventoryFind) {
      await this.loadInventoryList();
    }
  },
  methods: {
    /**
     * 初期データを取得する
     * 棚卸情報、メーカー一覧、カテゴリ一覧を取得
     * @returns {boolean} 棚卸情報の取得に成功した場合true
     */
    async getInitData() {
      showLoading();
      try {
        const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
        
        // 現在の棚卸情報を取得
        const inventoryResponse = await service.getCurrentInventory(userStoreId);
        if (inventoryResponse.data.success) {
          // 初回の棚卸情報が存在しない場合は以降の処理を行わない
          if (!inventoryResponse.data.inventoryId) {
            return false;
          }
          this.currentInventoryId = inventoryResponse.data.inventoryId;
        }
        
        // メーカー一覧を取得
        const makerResponse = await service.getMakerList();
        this.manufacturerOptions = makerResponse.data.makers.map(maker => {
          return { value: maker.id.toString(), label: maker.maker };
        });
        
        // カテゴリ一覧を取得
        const categoryResponse = await service.getCategoryList();
        this.categoryOptions = [];
        for (const parent of categoryResponse.data.categories) {
          for (const child of parent.childCategories) {
            this.categoryOptions.push({
              value: child.id.toString(),
              label: `${child.category}`,
              isInventory: child.isInventory
            });
          }
        }
        return true;
      } catch (error) {
        console.error('初期情報の取得に失敗しました', error);
        alert('初期情報の取得中にエラーが発生しました');
        return false;
      } finally {
        hideLoading()
      }
    },

    /**
     * 棚卸一覧を取得する
     */
    async loadInventoryList() {
      showLoading()
      try {
        const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
        const params = new URLSearchParams();
        
        // 検索パラメータを設定
        params.append('storeId', userStoreId);
        params.append('showScannedOnly', this.searchParams.showScannedOnly);
        params.append('page', this.currentPage);
        params.append('perPage', this.perPage);
        const response = await service.getInventoryList(params);
        
        if (response.data.success) {
          if (response.data.items == null) {
            this.inventoryList = [];
            this.totalItems = 0;
            this.isInventoryComplete = true;
            this.latestInventoryDate = '';
            return;
          }
          // 棚卸一覧データを整形
          this.inventoryList = response.data.items.map((item, index) => {
            const categoryName = this.getCategoryName(item.categoryId);
            const makerName = this.getMakerName(item.makerId);
            // カラム番号を設定（降順）
            const no = response.data.totalItems - ((this.currentPage) * this.perPage) - index;
            // 販売済の商品の背景色設定フラグを設定
            const backGroundColorPink = item.actualInventoryNumber == constants.SOLD_STOCK_QUANTITY;
            return {
              ...item,
              no: no,
              categoryName: categoryName,
              makerName: makerName,
              backGroundColorPink: backGroundColorPink
            };
          });
          this.totalItems = response.data.totalItems;
          this.isInventoryComplete = response.data.isInventoryComplete === 1;
          // 棚卸日付をフォーマット
          if (response.data.inventoryDate) {
            const date = new Date(response.data.inventoryDate);
            this.latestInventoryDate = `${date.getFullYear()}年${String(date.getMonth() + 1).padStart(2, '0')}月${String(date.getDate()).padStart(2, '0')}日`;
          }
        } else {
          alert(response.data.message)
        }
      } catch (error) {
        console.error('棚卸一覧の取得に失敗しました', error);
        alert('棚卸一覧の取得中にエラーが発生しました');
      } finally {
        hideLoading()
      }
    },
    
    /**
     * 棚卸一覧を検索する
     */
    async searchInventory() {
      this.currentPage = 0
      await this.loadInventoryList()
    },
    
    /**
     * ページ変更時の処理
     * @param {number} page - 変更後のページ番号
     */
    async onPageChange(page) {
      this.currentPage = page - 1
      await this.loadInventoryList()
    },
    
    /**
     * 棚卸差異表をCSVファイルでダウンロードする
     */
    async downloadInventory() {
      try {
        showLoading();
        const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
        
        // 棚卸差異表のCSVデータを取得
        const response = await service.downloadInventoryDiscrepancy(userStoreId);
        
        // 取得してデータを元にCSVファイルを作成し、ダウンロードする
        const blob = new Blob([response.data], { type: 'text/csv;charset=shift_jis' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        a.download = `棚卸差異表_${today}_${this.userStoreName}.csv`;
        
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        alert('棚卸差異表のダウンロードに失敗しました');
        console.error('Download error:', error);
      } finally {
        hideLoading();
      }
    },
    
    /**
     * 在庫一括更新を実行する
     * バーコードを入力したもの以外は在庫無しとして更新
     */
    async bulkUpdateInventory() {
      if (!this.currentInventoryId) {
        alert('棚卸情報が見つかりません');
        return;
      }
      if (!confirm('バーコードを入力したもの以外は【在庫無し】になります。\n更新してもよろしいですか？')) {
        return;
      }

      showLoading();
      try {
        // 在庫一括更新処理を行い、棚卸を完了させる
        const response = await service.bulkInventoryUpdate(this.currentInventoryId);
        
        if (response.data.success) {
          alert('在庫一括更新が完了しました');
          // 更新後に一覧を再読み込み
          await this.loadInventoryList();
        } else {
          alert(response.data.message || '在庫一括更新に失敗しました');
        }
      } catch (error) {
        alert('在庫一括更新中にエラーが発生しました');
        console.error('在庫一括更新に失敗しました', error);
      } finally {
        hideLoading();
      }
    },

    /**
     * カテゴリIDからカテゴリ名を取得する
     * @param {number} categoryId - カテゴリID
     * @returns {string} カテゴリ名
     */
    getCategoryName(categoryId) {
      const category = this.categoryOptions.find(cat => cat.value === categoryId?.toString());
      return category ? category.label : '';
    },

    /**
     * メーカーIDからメーカー名を取得する
     * @param {number} makerId - メーカーID
     * @returns {string} メーカー名
     */
    getMakerName(makerId) {
      const maker = this.manufacturerOptions.find(maker => maker.value === makerId?.toString());
      return maker ? maker.label : '';
    },

    /**
     * 商品コードを処理して棚卸詳細を登録する
     */
    async processProductCode() {
      const productCode = this.searchParams.productCode;
      
      try {
        showLoading();
        
        // 商品情報を検索
        const response = await service.searchProductForInventory(productCode, this.currentInventoryId);
        if (response.data.product == null) {
          alert('該当する商品が見つかりませんでした');
          this.clearProductCodeAndRefresh();
          return;
        }
        
        const product = response.data.product;
        const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
        
        // カテゴリが棚卸対象かチェック
        if (product.isTargetCategory !== constants.IS_TARGET) {
          alert('この商品のカテゴリは棚卸対象外です');
          this.clearProductCodeAndRefresh();
          return;
        }
        
        // 商品の現在店舗とログイン店舗が異なる場合の確認
        if (product.currentStoreId !== userStoreId) {
          if (!confirm('商品の現在店舗がログインアカウントの所属店舗と異なります。登録しますか？')) {
            this.clearProductCodeAndRefresh();
            return;
          }
        }
        
        // 棚卸詳細を登録
        await this.registerInventoryDetail(product);
        
      } catch (error) {
        alert('商品検索中にエラーが発生しました');
        console.error('Product search error:', error);
      } finally {
        hideLoading();
      }
    },

    /**
     * 棚卸詳細を登録する
     * @param {Object} product - 商品情報
     */
    async registerInventoryDetail(product) {
      try {
        const userStoreId = this.$store.getters['auth/currentUser']?.storeId;
        
        // 棚卸詳細登録用のリクエストデータを作成
        const requestData = {
          inventoryId: this.currentInventoryId,
          productId: product.id,
          userStoreId: userStoreId
        };
        
        await service.registerInventoryDetail(requestData);
        this.clearProductCodeAndRefresh();

      } catch (error) {
        console.error('棚卸詳細登録に失敗しました', error);
        alert('棚卸詳細登録中にエラーが発生しました');
      }
    },

    /**
     * 商品コードをクリアして一覧を更新する
     */
    clearProductCodeAndRefresh() {
      this.searchParams.productCode = '';
      this.currentPage = 0;
      this.loadInventoryList();
    }
  }
}
</script>

<style scoped>
.store-info {
  display: flex;
  align-items: center;
  gap: 20px;
  margin: 5px;
  padding: 10px 30px;
  font-weight: 700;
}

.store-name {
  width: 200px;
  margin-right: 10px;
}

.btn-secondary {
  background-color: #ffffff;
  color: #195983;
}

.btn-secondary:hover {
  background-color: #f0f0f0;
}
</style>
